import {
  __TYPE__,
  ABLE_TO_CHECKIN_BEFORE_MINUTES,
  APP_MODE,
  Appointment,
  APPOINTMENT_STATUS,
  AppointmentServiceItem,
  AutoGeneratedAvatarData,
  AvailableDate,
  AvailableSlots,
  BasicAppointmentSettings,
  BOOKING_TYPE,
  BusinessObject,
  BusinessObjectVisitProfile,
  Customer,
  CUSTOMER_TYPE_NAME,
  DATE_FORMAT,
  DEFAULT_CUSTOMER_NAME,
  FIRST_AVAILABLE_STAFF_ID,
  FORBIDDEN_ACTION,
  Product,
  Staff,
  TimeSlots,
  WEEKDAY,
} from '../models';
import { StringUtils } from './string.utils';
import { DateTimeUtils } from './date-time.utils';
import { ColorUtils } from './color.utils';
import { BusinessObjectUtils } from './business-object.utils';
import { EnumUtils } from './enum.utils';

export enum CUSTOMER_COMMUNICATION_TYPE {
  DISABLED = 'DISABLED',
  VIA_ANY = 'VIA_ANY',
  ONLY_VIA_EMAIL = 'ONLY_VIA_EMAIL',
  ONLY_VIA_SMS = 'ONLY_VIA_SMS',
}

export class CustomerUtils {
  private static readonly CANCELLED_STATUS = [
    APPOINTMENT_STATUS.BO_CANCELLED,
    APPOINTMENT_STATUS.CUSTOMER_CANCELLED,
  ];
  private static readonly CANCELLABLE_STATUS = [
    APPOINTMENT_STATUS.BLOCKER,
    APPOINTMENT_STATUS.CONFIRMED,
  ];

  private static readonly END_STATUS = [
    APPOINTMENT_STATUS.BO_CANCELLED,
    APPOINTMENT_STATUS.CUSTOMER_CANCELLED,
    APPOINTMENT_STATUS.FULFILLED,
    APPOINTMENT_STATUS.EXPIRED,
  ];

  private static readonly EDITABLE_STATUS = [
    APPOINTMENT_STATUS.BLOCKER,
    APPOINTMENT_STATUS.CONFIRMED,
  ];

  private static readonly EDITABLE_TYPE = [
    BOOKING_TYPE.BLOCKER,
    BOOKING_TYPE.AGENT,
    BOOKING_TYPE.TELEPHONE,
    BOOKING_TYPE.ONLINE,
    BOOKING_TYPE.RWG,
  ];

  private static readonly WEEKDAY_LIST = [
    WEEKDAY.SUNDAY,
    WEEKDAY.MONDAY,
    WEEKDAY.TUESDAY,
    WEEKDAY.WEDNESDAY,
    WEEKDAY.THURSDAY,
    WEEKDAY.FRIDAY,
    WEEKDAY.SATURDAY,
  ];

  // region Customer

  public static getMapOfIdAndCustomer(
    customers: Customer[]
  ): Map<number, Customer> {
    return new Map<number, Customer>(customers?.map((c) => [c.id, c]));
  }

  public static transformCustomerListForFrontend(
    customers: Customer[]
  ): Customer[] {
    if (!customers?.length) {
      return customers;
    }

    customers = customers.map((c) => this.transformCustomerForFrontend(c));

    customers.sort((c1, c2) => {
      if (c1.lastCheckInAsDate < c2.lastCheckInAsDate) {
        return 1;
      }
      if (c1.lastCheckInAsDate > c2.lastCheckInAsDate) {
        return -1;
      }
      return 0;
    });

    return customers;
  }

  public static transformCustomerForFrontend(customer: Customer): Customer {
    if (!customer) {
      return customer;
    }

    return {
      ...customer,
      birthdayAsDate: customer.birthday
        ? DateTimeUtils.parseStringToDate(customer.birthday)
        : null,
      lastCheckInAsDate: customer.lastCheckIn
        ? DateTimeUtils.parseStringToDate(customer.lastCheckIn)
        : null,
      registeredAtAsDate: DateTimeUtils.parseStringToDate(
        customer.registeredAt
      ),
      businessObjectVisitProfiles: customer.businessObjectVisitProfiles?.map(
        (visitProfile) => this.transformBoVisitProfileForFrontend(visitProfile)
      ),
      appointments: customer.appointments?.map(
        (appointment) =>
          ({
            ...appointment,
            startTimeAsDate:
              appointment.startTime &&
              DateTimeUtils.parseStringToDate(appointment.startTime),
          } as Appointment)
      ),
      color: ColorUtils.getCustomerColorToDisplay(customer.type?.name),
      avatarTextColor: ColorUtils.getCustomerAvatarTextColorToDisplay(
        customer.type?.name
      ),
    };
  }

  public static transformBoVisitProfileForFrontend(
    visitProfile: BusinessObjectVisitProfile
  ): BusinessObjectVisitProfile {
    return {
      ...visitProfile,
      lastCheckInAsDate: DateTimeUtils.parseStringToDate(
        visitProfile.lastCheckIn
      ),
      registeredAtAsDate: DateTimeUtils.parseStringToDate(
        visitProfile.registeredAt
      ),
    };
  }

  public static transformCustomerForBackend(customer: Customer): Customer {
    return {
      ...customer,
      __type__: customer.__type__ || __TYPE__.BASIC_CUSTOMER,
      birthday: customer.birthdayAsDate
        ? DateTimeUtils.formatDateToString(
            customer.birthdayAsDate,
            DATE_FORMAT.DATE_FORMAT_BACKEND
          )
        : null,
    };
  }

  public static generateCustomerFromKeyword(searchKeyword: string): Customer {
    const defaultName = DEFAULT_CUSTOMER_NAME;

    if (!StringUtils.simplifyString(searchKeyword)?.length) {
      return {
        name: defaultName,
      };
    }

    if (StringUtils.isEmailValid(searchKeyword)) {
      // search keyword is email
      return {
        name: defaultName,
        email: StringUtils.simplifyString(searchKeyword),
      };
    }

    if (StringUtils.removeAllNonNumberCharacters(searchKeyword)?.length) {
      // search keyword is phone number
      return {
        name: defaultName,
        phoneNr: StringUtils.simplifyString(searchKeyword),
      };
    }

    // search keyword is name of customer
    const nameArr: string[] = searchKeyword?.split(' ');

    if (nameArr?.length === 1) {
      return {
        name: searchKeyword,
        surname: searchKeyword,
      };
    }

    const surname = nameArr[nameArr.length - 1];
    const name = nameArr.slice(0, nameArr.length - 1)?.join(' ');
    return {
      name,
      surname,
    };
  }

  public static getAvatarDataForCustomer(
    customer: Customer
  ): AutoGeneratedAvatarData {
    return {
      avatarBackgroundColor: customer.color,
      avatarTextColor: customer.avatarTextColor,
      avatarText: CustomerUtils.getShortNameProfile(
        customer.name,
        customer.surname
      ),
    };
  }

  public static getShortNameProfile(name: string, surname: string): string {
    let shortName = '';
    if (surname && surname.length) {
      if (surname.length > 1) {
        shortName = surname[0].toUpperCase() + surname[1].toLowerCase();
      } else {
        shortName = surname[0].toUpperCase();
      }
    }
    if (name && name.length) {
      if (shortName.length) {
        shortName = name[0].toUpperCase() + shortName[0];
      } else if (name.length > 1) {
        shortName = name[0].toUpperCase() + name[1].toLowerCase();
      } else {
        shortName = name[0].toUpperCase();
      }
    }
    return shortName;
  }

  private static isMatchedCommunicationType(
    customer: Customer,
    commType: CUSTOMER_COMMUNICATION_TYPE
  ): boolean {
    if (commType === CUSTOMER_COMMUNICATION_TYPE.DISABLED) {
      return !customer?.notificationSubscribed;
    } else if (!customer?.notificationSubscribed) {
      return false;
    }

    if (commType === CUSTOMER_COMMUNICATION_TYPE.VIA_ANY) {
      return true;
    }

    const isCommunicationSmsActive =
      !!customer?.phoneNr?.length && customer?.phoneNrConfirmed;
    const isCommunicationEmailActive = !!customer?.email?.length;

    if (commType === CUSTOMER_COMMUNICATION_TYPE.ONLY_VIA_SMS) {
      return isCommunicationSmsActive && !isCommunicationEmailActive;
    }

    if (commType === CUSTOMER_COMMUNICATION_TYPE.ONLY_VIA_EMAIL) {
      return !isCommunicationSmsActive && isCommunicationEmailActive;
    }

    return false;
  }

  public static isMatchedAnyCommunicationType(
    customer: Customer,
    commTypes: CUSTOMER_COMMUNICATION_TYPE[]
  ): boolean {
    if (!commTypes?.length) {
      return true;
    }

    return (
      commTypes?.findIndex((t) =>
        this.isMatchedCommunicationType(customer, t)
      ) >= 0
    );
  }

  public static isMatchedName(
    searchValue: string,
    name: string,
    surname: string
  ): boolean {
    const validatedSearchValue = StringUtils.simplifyString(searchValue);
    if (validatedSearchValue && validatedSearchValue !== '') {
      const validatedName = StringUtils.simplifyString(name);
      const validatedSurname = StringUtils.simplifyString(surname);
      if (
        `${validatedSurname}${validatedName}`.indexOf(validatedSearchValue) >= 0
      ) {
        return true;
      }
      if (
        `${validatedName}${validatedSurname}`.indexOf(validatedSearchValue) >= 0
      ) {
        return true;
      }
      return false;
    } else {
      return true;
    }
  }

  public static isMatchedNameOrPhoneNrOrEmail(
    customer: Customer,
    searchKey: string
  ): boolean {
    let searchTermAsNr;
    try {
      searchTermAsNr = Number(searchKey);
    } catch (e) {}

    return (
      CustomerUtils.isMatchedName(searchKey, customer.name, customer.surname) ||
      customer.phoneNr?.includes(StringUtils.simplifyString(searchKey)) ||
      customer.phoneNr?.includes(`${searchTermAsNr}`) ||
      StringUtils.simplifyString(customer.email)?.includes(
        StringUtils.simplifyString(searchKey)
      )
    );
  }

  public static isMatchedType(
    customer: Customer,
    type: CUSTOMER_TYPE_NAME
  ): boolean {
    if (!type) {
      return true;
    }

    return customer.type.name === type;
  }

  public static isMatchedLastVisitTime(
    customer: Customer,
    boId: number,
    lastVisitFrom: Date = null,
    lastVisitTo: Date = null
  ): boolean {
    if (!lastVisitFrom && !lastVisitTo) {
      return true;
    }

    const matchedBoProfile = customer?.businessObjectVisitProfiles?.find(
      (b) => b.businessObjectId === boId
    );

    const lastCheckIn: Date =
      matchedBoProfile?.lastCheckInAsDate || customer?.lastCheckInAsDate;
    const registeredAt: Date =
      matchedBoProfile?.registeredAtAsDate || customer?.registeredAtAsDate;

    const dateToCheck = lastCheckIn || registeredAt;

    if (!dateToCheck) {
      return true;
    }

    if (!lastVisitTo) {
      return dateToCheck >= lastVisitFrom;
    }

    if (!lastVisitFrom) {
      return dateToCheck <= lastVisitTo;
    }

    return dateToCheck <= lastVisitTo && dateToCheck >= lastVisitFrom;
  }

  public static isAnonymousCustomer(
    customer: Customer,
    salonPhoneNr: string
  ): boolean {
    return customer?.phoneNr === salonPhoneNr;
  }

  public static isAbleToCheckin(customer: Customer): boolean {
    return (
      !customer?.lastCheckInAsDate ||
      DateTimeUtils.addMinuteToDate(customer.lastCheckInAsDate, 5) < new Date()
    );
  }

  // endregion

  // region Appointment

  public static isAppointmentCancelled(appointment: Appointment): boolean {
    return this.CANCELLED_STATUS.includes(appointment?.status);
  }

  public static isAppointmentCheckedIn(appointment: Appointment): boolean {
    return appointment?.status === APPOINTMENT_STATUS.CHECKED_IN;
  }

  public static isAbleToCheckinAppointment(appointment: Appointment): boolean {
    if (appointment?.status !== APPOINTMENT_STATUS.CONFIRMED) {
      return false;
    }

    const currentTime = new Date();
    const appointmentStartTime = DateTimeUtils.parseStringToDate(
      appointment.startTime
    );

    const checkinTime =
      appointmentStartTime.getTime() -
      ABLE_TO_CHECKIN_BEFORE_MINUTES * 60 * 1000;

    return checkinTime <= currentTime.getTime();
  }

  public static isAbleToCancelAppointment(appointment: Appointment): boolean {
    return this.CANCELLABLE_STATUS.includes(appointment?.status);
  }

  public static isAbleToEditAppointment(appointment: Appointment): boolean {
    return (
      this.EDITABLE_STATUS.includes(appointment?.status) &&
      this.EDITABLE_TYPE.includes(appointment?.bookingType)
    );
  }

  public static isEditActionForbiddenForStaffApp(
    mode: APP_MODE,
    staffIdOfAppointment: number,
    primaryStaffId: number,
    forbiddenActions: FORBIDDEN_ACTION[]
  ): boolean {
    if (mode !== APP_MODE.STAFF || !forbiddenActions?.length) {
      return false;
    }

    const isCurrentBlockerBelongToStaff =
      staffIdOfAppointment === primaryStaffId;
    if (isCurrentBlockerBelongToStaff) {
      return forbiddenActions?.includes(
        FORBIDDEN_ACTION.MANAGE_OWN_APPOINTMENTS
      );
    }

    return forbiddenActions?.includes(
      FORBIDDEN_ACTION.MANAGE_OTHERS_APPOINTMENTS
    );
  }

  public static isCreateActionForbiddenForStaffApp(
    mode: APP_MODE,
    forbiddenActions: FORBIDDEN_ACTION[]
  ): boolean {
    if (mode !== APP_MODE.STAFF || !forbiddenActions?.length) {
      return false;
    }

    return (
      forbiddenActions?.includes(FORBIDDEN_ACTION.MANAGE_OWN_APPOINTMENTS) &&
      forbiddenActions?.includes(FORBIDDEN_ACTION.MANAGE_OTHERS_APPOINTMENTS)
    );
  }

  public static isAppointmentHasEndStatus(appointment: Appointment): boolean {
    return this.END_STATUS.includes(appointment?.status);
  }

  public static getTotalPriceOfAppointmentItems(
    appointment: Appointment
  ): number {
    let total = 0;
    appointment?.services?.forEach((s) => (total += s.price));
    return total;
  }

  public static getTotalDurationOfAppointmentItems(
    appointment: Appointment
  ): number {
    let total = 0;
    appointment?.services?.forEach((s) => (total += s.duration));
    return total;
  }

  public static isClosedTime(
    appointmentSettings: BasicAppointmentSettings,
    date: Date
  ): boolean {
    if (!appointmentSettings?.activated) {
      return true;
    }

    if (this.isInSpecialClosingTimeRange(appointmentSettings, date)) {
      return true;
    }

    if (this.isInSpecialClosingTimeWeekday(appointmentSettings, date)) {
      return true;
    }

    if (this.isInSpecialOpeningTime(appointmentSettings, date)) {
      return false;
    }

    return !this.isInStandardOpeningTime(appointmentSettings, date);
  }

  private static isInSpecialClosingTimeRange(
    appointmentSettings: BasicAppointmentSettings,
    date: Date
  ): boolean {
    const matchedRules = appointmentSettings?.rules?.filter(
      (rule) =>
        !rule.activated &&
        rule.from &&
        rule.to &&
        date >=
          DateTimeUtils.getBeginOfDate(
            DateTimeUtils.parseStringToDate(rule.from)
          ) &&
        date <=
          DateTimeUtils.getEndOfDate(DateTimeUtils.parseStringToDate(rule.to))
    );

    return matchedRules?.length > 0;
  }

  private static isInSpecialClosingTimeWeekday(
    appointmentSettings: BasicAppointmentSettings,
    date: Date
  ): boolean {
    const weekday = this.WEEKDAY_LIST[date.getDay()];

    const matchedRules = appointmentSettings?.rules?.filter(
      (rule) => rule.weekday === weekday && !rule.activated
    );

    return matchedRules?.length > 0;
  }

  private static isInSpecialOpeningTime(
    appointmentSettings: BasicAppointmentSettings,
    date: Date
  ): boolean {
    const weekday = this.WEEKDAY_LIST[date.getDay()];
    const timeAsString = DateTimeUtils.formatTimeToString(date);

    const matchedRules = appointmentSettings?.rules?.filter(
      (rule) =>
        rule.weekday === weekday &&
        rule.activated &&
        rule.capacity &&
        rule.open &&
        rule.close &&
        timeAsString >= rule.open &&
        timeAsString <= rule.close
    );

    return matchedRules?.length > 0;
  }

  private static isInStandardOpeningTime(
    appointmentSettings: BasicAppointmentSettings,
    date: Date
  ): boolean {
    if (!date || !appointmentSettings?.open || !appointmentSettings?.close) {
      return false;
    }

    const timeAsString = DateTimeUtils.formatTimeToString(date);
    return (
      timeAsString >= appointmentSettings.open &&
      timeAsString <= appointmentSettings.close
    );
  }

  public static getAllOpeningWeekday(
    appointmentSettings: BasicAppointmentSettings
  ): WEEKDAY[] {
    const closingWeekdays = [];

    appointmentSettings?.rules?.forEach((rule) => {
      if (!rule.activated) {
        // if a rule for weekday is not defined, it will be considered as opening
        closingWeekdays.push(rule.weekday);
      }
    });

    return EnumUtils.convertEnumToArray(WEEKDAY)?.filter(
      (w) => !closingWeekdays.includes(w)
    ) as WEEKDAY[];
  }

  public static getEarliestOpenTime(
    appointmentSettings: BasicAppointmentSettings
  ): string {
    let openTime: string;

    appointmentSettings?.rules?.forEach((r) => {
      if (r?.activated && r.open && (!openTime || r.open < openTime)) {
        openTime = r.open;
      }
    });

    if (
      !openTime ||
      (appointmentSettings?.rules?.length !== 7 &&
        appointmentSettings.open < openTime)
    ) {
      openTime = appointmentSettings?.open;
    }

    return openTime;
  }

  public static getLatestCloseTime(
    appointmentSettings: BasicAppointmentSettings
  ): string {
    let closeTime: string;

    appointmentSettings?.rules?.forEach((r) => {
      if (r?.activated && r.close && (!closeTime || r.close > closeTime)) {
        closeTime = r.close;
      }
    });

    if (
      !closeTime ||
      (appointmentSettings?.rules?.length !== 7 &&
        appointmentSettings?.close > closeTime)
    ) {
      closeTime = appointmentSettings?.close;
    }

    return closeTime;
  }

  public static getMaxDurationOpenTime(
    appointmentSettings: BasicAppointmentSettings
  ): number {
    let maxDurationInMinutes = 0;

    appointmentSettings?.rules?.forEach((r) => {
      if (r?.activated) {
        const duration = this.calculateDurationInMinute(r.open, r.close);
        if (duration > maxDurationInMinutes) {
          maxDurationInMinutes = duration;
        }
      }
    });

    if (appointmentSettings?.rules?.length !== 7) {
      const standardDuration = this.calculateDurationInMinute(
        appointmentSettings?.open,
        appointmentSettings?.close
      );

      if (standardDuration > maxDurationInMinutes) {
        maxDurationInMinutes = standardDuration;
      }
    }

    return maxDurationInMinutes;
  }

  private static calculateDurationInMinute(
    openTime: string,
    closeTime: string
  ): number {
    return (
      this.convertTimeStrToMinute(closeTime) -
      this.convertTimeStrToMinute(openTime)
    );
  }

  private static convertTimeStrToMinute(timeStr: string): number {
    try {
      const timeArr = timeStr?.split(':');

      if (timeStr?.length < 2) {
        return 0;
      }

      return Number(timeArr[0]) * 60 + Number(timeArr[1]);
    } catch (e) {
      return 0;
    }
  }

  public static fillRecommendedStaff(
    appointments: Appointment[],
    staffs: Staff[]
  ): Appointment[] {
    const mapOfDateAndAppointments = new Map<string, Appointment[]>();
    appointments.forEach((a) => {
      const startDate = DateTimeUtils.formatDateToString(
        a.startTimeAsDate,
        DATE_FORMAT.DATE_FORMAT_BACKEND
      );
      const currentAppointments = mapOfDateAndAppointments.get(startDate) || [];
      mapOfDateAndAppointments.set(startDate, [...currentAppointments, a]);
    });

    const result: Appointment[] = [];

    mapOfDateAndAppointments.forEach((appointments) => {
      const filledAppointments =
        this.fillRecommendedStaffToAllAppointmentsOfTheSameDate(
          appointments,
          staffs
        ) || [];
      result.push(...filledAppointments);
    });

    return result;
  }

  /**
   * Algorithm to assign staff to ownerless appointments
   * When assigning staffs, the workload of staff is considered (workload = total duration of appointments of staff)
   * - Staff in recommendedStaffs is selected
   * - Staff is selected in order: staff enabled for booking, active staff, inactive staff
   * - Then the staff with the lowest workload is assigned to the appointment
   *
   * The unassigned appointments will always be sorted by start time and end time (earliest start time and later end time first)
   *
   * @param appointments {Appointment[]}: all appointments of a date
   * @param staffs {Staff[]}
   * @returns {Appointment[]}
   * */
  private static fillRecommendedStaffToAllAppointmentsOfTheSameDate(
    appointments: Appointment[],
    staffs: Staff[]
  ): Appointment[] {
    const mapOfAppointmentIdAndRecommendedStaffId = new Map<number, Staff>();
    const staffAppointments: Map<number, Appointment[]> = new Map();
    const staffWorkloads: Map<number, number> = new Map();

    // Sort staffs
    staffs.sort((s1, s2) => {
      if (!s1.active && s2.active) {
        return 1;
      }

      if (!s2.active && s1.active) {
        return -1;
      }

      if (!s1.enabled4Booking) {
        return 1;
      }

      if (!s2.enabled4Booking) {
        return -1;
      }

      return 0;
    });

    // Initialize staff data
    staffs.forEach((s) => {
      staffAppointments.set(s.id, []);
      staffWorkloads.set(s.id, 0);
    });
    // appointment may include deleted staff
    appointments?.forEach((a) => {
      if (a.staffId) {
        staffAppointments.set(a.staffId, []);
        staffWorkloads.set(a.staffId, 0);
      }
      a.specialistIdsCanDoMostService =
        this.getTheBestSpecialistIdsOfAppointment(a);
    });

    const unassignedAppointments: Appointment[] = [];

    // Process already assigned appointments
    appointments.forEach((a) => {
      const staff = staffs?.find((s) => s?.id === a?.staffId);

      // consider an appointment of deleted/inactive staff as an ownerless appointment
      if (!staff?.active) {
        unassignedAppointments.push(a);
      } else {
        const list = staffAppointments.get(a.staffId)!;
        list.push(a);
        list.sort((a, b) => a.startTime!.localeCompare(b.startTime));
        staffAppointments.set(a.staffId, list);

        const currentWorkloads = staffWorkloads.get(a.staffId);
        staffWorkloads.set(
          a.staffId,
          currentWorkloads +
            DateTimeUtils.getDiffInMinute(a.endTimeAsDate, a.startTimeAsDate)
        );
      }
    });

    unassignedAppointments.sort((a, b) => {
      // greedy algorithm: appointment with earlier start time first
      if (a.startTime < b.startTime) {
        return -1;
      }
      if (a.startTime > b.startTime) {
        return 1;
      }
      // greedy algorithm: appointment with higher duration first
      if (a.endTime > b.endTime) {
        return -1;
      }
      if (a.endTime < b.endTime) {
        return 1;
      }

      // appointment with special products having less specialist has a higher priority
      const numberOfBestSpecialistA =
        a?.specialistIdsCanDoMostService?.length || Infinity;
      const numberOfBestSpecialistB =
        b?.specialistIdsCanDoMostService?.length || Infinity;
      if (numberOfBestSpecialistA > numberOfBestSpecialistB) {
        return -1;
      }
      if (numberOfBestSpecialistA < numberOfBestSpecialistB) {
        return 1;
      }

      // the real ownerless appointments have higher priority
      // than the appointments of deleted/inactive staff
      const isAOwnerlessAppointment = !a.staffId;
      const isBOwnerlessAppointment = !b.staffId;

      if (isAOwnerlessAppointment && !isBOwnerlessAppointment) {
        return -1;
      }

      if (isBOwnerlessAppointment && !isAOwnerlessAppointment) {
        return 1;
      }

      const staffA = staffs?.find((s) => s?.id === a?.staffId);
      const staffB = staffs?.find((s) => s?.id === b?.staffId);

      if (!staffA && !!staffB) {
        return -1;
      }

      if (!staffB && !!staffA) {
        return 1;
      }

      if (!staffA?.active && !!staffB?.active) {
        return -1;
      }

      if (!staffB?.active && !!staffA?.active) {
        return 1;
      }

      if (!staffA?.enabled4Booking && !!staffB?.enabled4Booking) {
        return -1;
      }

      if (!staffB?.enabled4Booking && !!staffA?.enabled4Booking) {
        return 1;
      }

      return 0;
    });

    // Assign unassigned appointments
    unassignedAppointments.forEach((appointment) => {
      let bestStaff: Staff | null = null;
      let minWorkload = Infinity;

      staffs.forEach((staff) => {
        const isStaffEnabledForBooking = staff.active && staff.enabled4Booking;

        // skip if staff is not enabled for booking and the best staff found
        if (isStaffEnabledForBooking || !bestStaff?.id) {
          const scheduledAppointments = staffAppointments.get(staff.id)!;

          // Check for time conflict
          const hasConflict = scheduledAppointments.some((a) =>
            DateTimeUtils.areTwoDateRangesOverlap(
              a.startTimeAsDate,
              a.endTimeAsDate,
              appointment.startTimeAsDate,
              appointment.endTimeAsDate
            )
          );

          if (!hasConflict) {
            const workload = staffWorkloads.get(staff.id)!;

            // if this is the first available staff or the first canDoMost/recommended staff -> assign
            const isBestStaffCanDoMost =
              appointment?.specialistIdsCanDoMostService?.includes(
                bestStaff?.id
              );
            const isThisStaffCanDoMost =
              appointment?.specialistIdsCanDoMostService?.includes(staff?.id);

            const isBestStaffRecommended =
              appointment?.recommendedStaffs?.includes(bestStaff?.id);
            const isThisStaffRecommended =
              appointment?.recommendedStaffs?.includes(staff?.id);

            const bestStaffPoint =
              (isBestStaffCanDoMost ? 1 : 0) + (isBestStaffRecommended ? 1 : 0);
            const thisStaffPoint =
              (isThisStaffCanDoMost ? 1 : 0) + (isThisStaffRecommended ? 1 : 0);

            if (
              !bestStaff?.id ||
              thisStaffPoint > bestStaffPoint ||
              (thisStaffPoint === bestStaffPoint && workload < minWorkload)
            ) {
              bestStaff = staff;
              minWorkload = workload;
            }
          }
        }
      });

      if (bestStaff?.id) {
        const list = staffAppointments.get(bestStaff.id)!;
        list.push(appointment);
        list.sort((a, b) => a.startTime!.localeCompare(b.startTime));
        staffAppointments.set(bestStaff.id, list);

        staffWorkloads.set(
          bestStaff.id,
          staffWorkloads.get(bestStaff.id)! +
            DateTimeUtils.getDiffInMinute(
              appointment.endTimeAsDate,
              appointment.startTimeAsDate
            )
        );

        mapOfAppointmentIdAndRecommendedStaffId.set(appointment.id, bestStaff);
      } else {
        // it cannot happen, but just in case
        console.warn(
          `Appointment ${appointment.id} could not be assigned (conflict for all staff).`
        );
        // auto assign to the first available staff
        mapOfAppointmentIdAndRecommendedStaffId.set(appointment.id, {
          id: FIRST_AVAILABLE_STAFF_ID,
        });
      }
    });

    return appointments?.map((a) => {
      if (mapOfAppointmentIdAndRecommendedStaffId.get(a.id)) {
        a.recommendedStaff = mapOfAppointmentIdAndRecommendedStaffId.get(a.id);
      }

      return a;
    });
  }

  /**
   * This method will return a list of staffId who can do most services of the given appointment
   * */
  private static getTheBestSpecialistIdsOfAppointment(
    appointment: Appointment
  ): number[] {
    // if the map is empty -> every staff can do all services of this appointment
    const mapOfSpecialistIdAndNumberOfPossibleServices = new Map<
      number,
      number
    >();

    appointment?.services?.forEach((serviceItem) => {
      if (serviceItem?.product?.specialists?.length) {
        serviceItem?.product?.specialists?.forEach((specialist) => {
          mapOfSpecialistIdAndNumberOfPossibleServices.set(
            specialist,
            (mapOfSpecialistIdAndNumberOfPossibleServices.get(specialist) ||
              0) + 1
          );
        });
      }
    });

    let topSpecialistIds: number[] = [];
    let maxNumberOfPossibleServices = 0;
    mapOfSpecialistIdAndNumberOfPossibleServices.forEach(
      (numberOfPossibleServices, specialistId) => {
        if (numberOfPossibleServices > maxNumberOfPossibleServices) {
          maxNumberOfPossibleServices = numberOfPossibleServices;
          topSpecialistIds = [specialistId];
        } else if (numberOfPossibleServices === maxNumberOfPossibleServices) {
          topSpecialistIds.push(specialistId);
        }
      }
    );

    return topSpecialistIds;
  }

  public static isAvailableSlotsContainDateTime(
    availableSlots: AvailableSlots,
    dateTime: Date
  ): boolean {
    const matchedTimeSlots = this.getSlotsOfDate(availableSlots, dateTime);
    return this.isTimeSlotsContainDateTime(matchedTimeSlots, dateTime);
  }

  private static isTimeSlotsContainDateTime(
    timeSlots: TimeSlots,
    dateTime: Date
  ): boolean {
    if (!dateTime || !timeSlots?.times?.length) {
      return false;
    }

    const timeStr = `${DateTimeUtils.formatTimeToString(dateTime, true)}:00`;
    return timeSlots?.times?.includes(timeStr);
  }

  private static getSlotsOfDate(
    availableSlots: AvailableSlots,
    dateTime: Date
  ): TimeSlots {
    if (!dateTime || !availableSlots) {
      return null;
    }

    const dateStr = DateTimeUtils.formatDateToString(
      dateTime,
      DATE_FORMAT.DATE_FORMAT_BACKEND
    );

    return availableSlots?.slotsOfDates?.find(
      (slotsOfDate) => slotsOfDate.date === dateStr
    );
  }

  public static getNearestTimeSlotOfDate(
    availableSlots: AvailableSlots,
    date: Date
  ): Date {
    if (!date) {
      return null;
    }

    let matchedSlotsOfDate = this.getSlotsOfDate(availableSlots, date);

    if (this.isTimeSlotsContainDateTime(matchedSlotsOfDate, date)) {
      return date;
    }

    if (!matchedSlotsOfDate?.times?.length) {
      // no available slots for designed date -> pick the first available slot
      matchedSlotsOfDate = availableSlots?.slotsOfDates?.find(
        (slotsOfDate) => slotsOfDate?.times?.length > 0
      );
    }

    const nearestDateSlot = matchedSlotsOfDate?.date;
    let nearestTimeSlot: string;

    // remove second of time. e.g. 09:00:41 -> 09:00:00
    const desiredTimeStr = `${DateTimeUtils.formatTimeToString(date, true)}:00`;

    matchedSlotsOfDate?.times?.forEach((slot) => {
      if (slot <= desiredTimeStr) {
        nearestTimeSlot = slot;
      } else if (!nearestTimeSlot) {
        nearestTimeSlot = slot;
      }
    });

    return DateTimeUtils.generateDateFromDateAndTimeString(
      nearestDateSlot,
      nearestTimeSlot
    );
  }

  public static fillSlotsOfDate(
    appointmentTimeSlot: AvailableSlots,
    fromDate: string,
    toDate: string
  ): AvailableSlots {
    if (!appointmentTimeSlot) {
      return null;
    }

    const mapOfDateAndSlots: Map<string, string[]> = new Map(
      appointmentTimeSlot.slotsOfDates?.map((sod) => [sod.date, sod.times])
    );

    const listOfDate: string[] = DateTimeUtils.getListOfDateString(
      fromDate,
      toDate
    );

    return {
      ...appointmentTimeSlot,
      from: fromDate,
      to: toDate,
      slotsOfDates: listOfDate.map((date) => ({
        date: date,
        times: mapOfDateAndSlots.get(date) || [],
      })),
    };
  }

  public static fillAvailableDate(
    availableDates: AvailableDate[],
    fromDate: string,
    toDate: string
  ): AvailableDate[] {
    const mapOfDateAndAvailableDate: Map<string, AvailableDate> = new Map(
      availableDates?.map((a) => [a.date, a])
    );

    const listOfDate: string[] = DateTimeUtils.getListOfDateString(
      fromDate,
      toDate
    );

    return listOfDate?.map((date) => {
      if (mapOfDateAndAvailableDate.has(date)) {
        return mapOfDateAndAvailableDate.get(date);
      }

      return {
        date,
      };
    });
  }

  public static generateAppointment(
    businessObject: BusinessObject,
    staffs: Staff[],
    startTime: Date,
    staffId: number,
    productIds: number[]
  ): Appointment {
    const serviceList = BusinessObjectUtils.getListOfProducts(
      businessObject,
      productIds
    )?.map(
      (product) =>
        ({
          __type__: __TYPE__.BASIC_APPOINTMENT_SERVICE,
          productId: product.id,
          name: product.name,
          price:
            startTime &&
            BusinessObjectUtils.isAnyDiscountActivatedAtTimeStamp(
              product,
              startTime
            )
              ? BusinessObjectUtils.getPromotionalPrice(product, startTime)
              : product.price,
          duration: product.duration,
        } as AppointmentServiceItem)
    );

    return {
      startTime: DateTimeUtils.formatDateTimeToString(
        startTime,
        DATE_FORMAT.DATE_FORMAT_BACKEND
      ),
      startTimeAsDate: startTime,
      staffId,
      services: serviceList,
      staff: staffs?.find((s) => s.id === staffId),
    };
  }

  public static updateBlockerBasingOnAvailableDate(
    blocker: Appointment,
    availableDate: AvailableDate
  ): Appointment {
    if (!blocker || !availableDate) {
      return blocker;
    }
    blocker.startTimeAsDate = DateTimeUtils.generateDateFromDateAndTimeString(
      availableDate.date,
      availableDate.start
    );
    blocker.startTime = DateTimeUtils.formatDateTimeToString(
      blocker.startTimeAsDate,
      DATE_FORMAT.DATE_FORMAT_BACKEND
    );
    blocker.endTimeAsDate = DateTimeUtils.generateDateFromDateAndTimeString(
      availableDate.date,
      availableDate.end
    );
    blocker.endTime = DateTimeUtils.formatDateTimeToString(
      blocker.endTimeAsDate,
      DATE_FORMAT.DATE_FORMAT_BACKEND
    );
    blocker.estimatedDuration = DateTimeUtils.getDiffInMinute(
      blocker.endTimeAsDate,
      blocker.startTimeAsDate
    );

    return blocker;
  }

  public static extendAppointment(
    appointment: Appointment,
    staff: Staff,
    mapOfIdAndProduct: Map<number, Product>,
    businessObject: BusinessObject
  ): Appointment {
    appointment.staff = staff;
    appointment.businessObject = businessObject;

    appointment.services = appointment.services?.map((s) => ({
      ...s,
      product: mapOfIdAndProduct.get(s.productId),
    }));

    return appointment;
  }

  public static transformAvailableSlotsForFrontend(
    availableSlots: AvailableSlots
  ): AvailableSlots {
    if (!availableSlots) {
      return null;
    }

    return {
      ...availableSlots,
      slotsOfDates: availableSlots?.slotsOfDates
        ?.map((s) => this.transformTimeSlotsForFrontend(s))
        ?.sort((a, b) => a.date.localeCompare(b.date)),
    };
  }

  public static transformAvailableDatesForFrontend(
    availableDates: AvailableDate[]
  ): AvailableDate[] {
    if (!availableDates?.length) {
      return [];
    }

    availableDates.sort((a, b) => a.date?.localeCompare(b.date));

    return availableDates;
  }

  public static transformTimeSlotsForFrontend(timeSlots: TimeSlots): TimeSlots {
    if (!timeSlots) {
      return null;
    }

    return {
      ...timeSlots,
      times: timeSlots?.times?.sort(),
    };
  }

  public static transformAppointmentForBackend(
    appointment: Appointment
  ): Appointment {
    return {
      ...appointment,
      __type__: appointment.__type__ || __TYPE__.BASIC_APPOINTMENT,
    };
  }

  public static transformAppointmentForFrontend(
    appointment: Appointment
  ): Appointment {
    if (!appointment) {
      return appointment;
    }

    return {
      ...appointment,
      messages: appointment.messages?.sort((a, b) =>
        a.createdTime.localeCompare(b.createdTime)
      ),
      createdTimeAsDate: appointment.createdTime
        ? DateTimeUtils.parseStringToDate(appointment.createdTime)
        : undefined,
      lastModifiedAsDate: appointment.lastModified
        ? DateTimeUtils.parseStringToDate(appointment.lastModified)
        : undefined,
      startTimeAsDate: appointment.startTime
        ? DateTimeUtils.parseStringToDate(appointment.startTime)
        : undefined,
      endTimeAsDate: appointment.endTime
        ? DateTimeUtils.parseStringToDate(appointment.endTime)
        : undefined,
      owner: this.transformCustomerForFrontend(appointment.owner),
    };
  }

  public static transformBookingSettingForFrontend(
    basicAppointmentSettings: BasicAppointmentSettings
  ): BasicAppointmentSettings {
    basicAppointmentSettings?.rules?.forEach((rule) => {
      rule.open = this.formatTime(rule.open);
      rule.close = this.formatTime(rule.close);
    });

    if (basicAppointmentSettings) {
      basicAppointmentSettings.open = this.formatTime(
        basicAppointmentSettings.open
      );
      basicAppointmentSettings.close = this.formatTime(
        basicAppointmentSettings.close
      );
    }

    return basicAppointmentSettings;
  }

  private static formatTime(time: string): string {
    const arr = time?.split(':');

    if (arr?.length > 1) {
      return `${arr[0]}:${arr[1]}`;
    } else {
      return time;
    }
  }

  // endregion
}
